import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWaitlist } from "../../store/thunks/waitlistThunks";
import WaitlistList from "./WaitlistList";
import WaitlistModal from "./WaitlistModal";
import Card from "../shared/Card";
import SearchBar from "../shared/SearchBar";
import Pagination from "../shared/Pagination";

const Waitlist = () => {
  // Function to convert waitlist data to CSV format
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    // CSV header
    const header = ['Email', 'Created At'];
    
    // CSV rows
    const rows = data.map(item => {
      const date = new Date(item.createdAt);
      const formattedDate = `${date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      })} ${date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit'
      })}`;
      
      return [
        item.email,
        formattedDate
      ];
    });
    
    // Combine header and rows
    const csvContent = [
      header.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    return csvContent;
  };
  
  // Function to download CSV file
  const handleExportCSV = () => {
    const csvData = convertToCSV(waitlist);
    if (!csvData) {
      alert('No data to export');
      return;
    }
    
    // Create a Blob with the CSV data
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    
    // Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    // Set link properties
    link.setAttribute('href', url);
    link.setAttribute('download', `waitlist-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    // Add link to document, click it, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const dispatch = useDispatch();
  const { waitlist, loading, error, pagination } = useSelector(
    (state) => state.waitlist
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadWaitlist();
  }, [dispatch, pagination.page, searchTerm, searchDate, startDate, endDate]);

  const loadWaitlist = () => {
    const params = {
      page: pagination.page,
      limit: pagination.limit,
    };

    // If date range is provided, use that for search
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
      // Clear other search parameters to avoid conflicts
      params.search = "";
      params.date = "";
    } else {
      // Otherwise use regular search parameters
      params.search = searchTerm;
      params.date = searchDate;
    }

    dispatch(fetchWaitlist(params));
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadWaitlist();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleSearch = (term) => {
    // Reset to page 1 when searching
    if (pagination.page !== 1) {
      dispatch({ type: "waitlist/setWaitlistPage", payload: 1 });
    }
    setSearchTerm(term);
    // Clear date search when using text search
    setSearchDate("");
  };

  const handleDateSearch = (e) => {
    const date = e.target.value;
    // Reset to page 1 when searching
    if (pagination.page !== 1) {
      dispatch({ type: "waitlist/setWaitlistPage", payload: 1 });
    }
    setSearchDate(date);
    // Clear text search when using date search
    setSearchTerm("");
  };

  const handlePageChange = (page) => {
    dispatch({ type: "waitlist/setWaitlistPage", payload: page });
  };

  const handleAddNew = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSave = () => {
    setIsModalOpen(false);
    loadWaitlist();
  };

  return (
    <div className="space-y-6 mt-16">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Waitlist Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage email subscriptions and waitlist entries
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            disabled={!waitlist || waitlist.length === 0}
          >
            <svg
              className="w-5 h-5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Export CSV
          </button>
          <button
            onClick={handleAddNew}
            className="px-4 py-2 bg-[#B85C38] text-white rounded-lg hover:bg-[#B85C38]/90 transition-colors flex items-center"
          >
            <svg
              className="w-5 h-5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add New Entry
          </button>
        </div>
      </div>

      <Card>
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
            <div className="w-full md:w-1/2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search by Email
              </label>
              <SearchBar
                value={searchTerm}
                onChange={(value) => setSearchTerm(value)}
                onSearch={handleSearch}
                placeholder="Search by email..."
              />
            </div>

            <div className="w-full md:w-1/2 mt-4 md:mt-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search by Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={searchDate}
                  onChange={handleDateSearch}
                  className="w-full px-4 py-2 border rounded-lg
                          border-[#E6CCB2] bg-white/80 backdrop-blur-sm
                          text-[#2E1F27] placeholder-[#9B7E6B]
                          focus:outline-none focus:ring-2 focus:ring-[#B85C38]
                          transition-all duration-300 ease-in-out"
                />
                {searchDate && (
                  <button
                    onClick={() => setSearchDate("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      className="w-5 h-5 mr-10"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
            <div className="w-full md:w-1/2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg
                          border-[#E6CCB2] bg-white/80 backdrop-blur-sm
                          text-[#2E1F27] placeholder-[#9B7E6B]
                          focus:outline-none focus:ring-2 focus:ring-[#B85C38]
                          transition-all duration-300 ease-in-out"
                />
                {startDate && (
                  <button
                    onClick={() => setStartDate("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      className="w-5 h-5 mr-10"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="w-full md:w-1/2 mt-4 md:mt-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg
                          border-[#E6CCB2] bg-white/80 backdrop-blur-sm
                          text-[#2E1F27] placeholder-[#9B7E6B]
                          focus:outline-none focus:ring-2 focus:ring-[#B85C38]
                          transition-all duration-300 ease-in-out"
                />
                {endDate && (
                  <button
                    onClick={() => setEndDate("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      className="w-5 h-5 mr-10"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={loadWaitlist}
              className="px-4 py-2 bg-[#B85C38] text-white rounded-lg hover:bg-[#B85C38]/90 transition-colors"
              disabled={!startDate || !endDate}
            >
              Search Date Range
            </button>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh list"
              disabled={isRefreshing}
            >
              <svg
                className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4 flex items-center">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {error}
          </div>
        )}

        <WaitlistList
          waitlist={waitlist}
          loading={loading}
          onRefresh={loadWaitlist}
        />

        {waitlist && waitlist.length > 0 && (
          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Showing {waitlist.length} of {pagination.total} entries
            </div>
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>

      {isModalOpen && (
        <WaitlistModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default Waitlist;
